import { find } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonType } from '../../Shared/Button';
import Button from '../../Shared/Button/Button';
import Loader from '../../Shared/Loader/Loader';
import './AddSchoolModal.scss';
import PhoneNumberBranding from '../../Branding/PhoneNumberBranding';
import Autosuggest from 'react-autosuggest';
import {
  ADD_SCHOOL_MODAL,
  PRODUCT_TYPE_LABEL_MAP,
} from '../../../settings/settings';
import Dropdown from '../../Shared/Dropdown/Dropdown';
import icon_chevron_up from '../../../assets/images/icon_chevron_up.svg';
import { TOOLTIP_POSITION_BOTTOM } from '../../../utils/dom-utils';
import ToolTip from '../../Shared/ToolTip/ToolTip';
import {
  getAllSchoolList,
  getIsSchoolListError,
} from '../../../redux/modules/addSchool/existingSchools.selector';
import { addNewSchoolToStore } from '../../../redux/modules/addSchool/addSchool.module';
import { getProductTypes } from '../../../redux/modules/productTypes/productType.selectors';
import {
  addSchoolValidationFields,
  handleMultipleValidations,
} from './Validations';
import ErrorPage from '../../Shared/FullPageLoader/ErrorPage';
import config from '../../../config/config';

const us_states_abbr = [
  { value: 'AL', label: 'Alabama (AL)' },
  { value: 'AK', label: 'Alaska (AK)' },
  { value: 'AS', label: 'American Samoa (AS)' },
  { value: 'AZ', label: 'Arizona (AZ)' },
  { value: 'AR', label: 'Arkansas (AR)' },
  { value: 'CA', label: 'California (CA)' },
  { value: 'CO', label: 'Colorado (CO)' },
  { value: 'CT', label: 'Connecticut (CT)' },
  { value: 'DE', label: 'Delaware (DE)' },
  { value: 'DC', label: 'District Of Columbia (DC)' },
  { value: 'FM', label: 'Federated States of Micronesia (FM)' },
  { value: 'FL', label: 'Florida (FL)' },
  { value: 'GA', label: 'Georgia (GA)' },
  { value: 'GU', label: 'Guam (GU)' },
  { value: 'HI', label: 'Hawaii (HI)' },
  { value: 'ID', label: 'Idaho (ID)' },
  { value: 'IL', label: 'Illinois (IL)' },
  { value: 'IN', label: 'Indiana (IN)' },
  { value: 'IA', label: 'Iowa (IA)' },
  { value: 'KS', label: 'Kansas (KS)' },
  { value: 'KY', label: 'Kentucky (KY)' },
  { value: 'LA', label: 'Louisiana (LA)' },
  { value: 'MH', label: 'Marshall Islands (MH)' },
  { value: 'ME', label: 'Maine (ME)' },
  { value: 'MD', label: 'Maryland (MD)' },
  { value: 'MA', label: 'Massachusetts (MA)' },
  { value: 'MI', label: 'Michigan (MI)' },
  { value: 'MN', label: 'Minnesota (MN)' },
  { value: 'MS', label: 'Mississippi (MS)' },
  { value: 'MO', label: 'Missouri (MO)' },
  { value: 'MT', label: 'Montana (MT)' },
  { value: 'NE', label: 'Nebraska (NE)' },
  { value: 'NV', label: 'Nevada (NV)' },
  { value: 'NH', label: 'New Hampshire (NH)' },
  { value: 'NJ', label: 'New Jersey (NJ)' },
  { value: 'NM', label: 'New Mexico (NM)' },
  { value: 'NY', label: 'New York (NY)' },
  { value: 'NC', label: 'North Carolina (NC)' },
  { value: 'ND', label: 'North Dakota (ND)' },
  { value: 'MP', label: 'Northern Mariana Islands (MP)' },
  { value: 'OH', label: 'Ohio (OH)' },
  { value: 'OK', label: 'Oklahoma (OK)' },
  { value: 'OR', label: 'Oregon (OR)' },
  { value: 'PW', label: 'Palau (PW)' },
  { value: 'PA', label: 'Pennsylvania (PA)' },
  { value: 'PR', label: 'Puerto Rico (PR)' },
  { value: 'RI', label: 'Rhode Island (RI)' },
  { value: 'SC', label: 'South Carolina (SC)' },
  { value: 'SD', label: 'South Dakota (SD)' },
  { value: 'TN', label: 'Tennessee (TN)' },
  { value: 'TX', label: 'Texas (TX)' },
  { value: 'VI', label: 'U.S. Virgin Islands (VI)' },
  { value: 'UT', label: 'Utah (UT)' },
  { value: 'VT', label: 'Vermont (VT)' },
  { value: 'VA', label: 'Virginia (VA)' },
  { value: 'WA', label: 'Washington (WA)' },
  { value: 'WV', label: 'West Virginia (WV)' },
  { value: 'WI', label: 'Wisconsin (WI)' },
  { value: 'WY', label: 'Wyoming (WY)' },
];

export const isValidFields = (schoolData, errors, isAutoSuggestError) => {
  return (
    schoolData.schoolName !== '' &&
    schoolData.addressLineOne !== '' &&
    schoolData.city !== '' &&
    schoolData.state !== '' &&
    schoolData.zip !== '' &&
    Object.values(errors).every((x) => x === false) &&
    !isAutoSuggestError
  );
};

const toolTipMessage = (
  <div>
    We will use the exact mailing information entered to mail the check to the
    selected school. If you have questions on where to send the check, please
    contact the school to confirm the mailing instructions.
    <br />
    <br />
    The student name and student ID for which the payment should be applied will
    be identified during Step 2 of the distribution process.
  </div>
);

const AddSchoolModal = ({ closeModal }) => {
  const dispatch = useDispatch();

  const [school, setSchool] = useState({
    schoolName: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    zip: '',
    zipExt: '',
  });
  const [displayMessage, setDisplayMessage] = useState(false);
  const [initialError, setInitialError] = useState(false);
  const [isAutoSuggestError, setIsAutoSuggestError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalError, setIsModalError] = useState(false);

  const productTypes = useSelector(getProductTypes);

  const schoolList = useSelector(getAllSchoolList);

  const modalHasError = useSelector(getIsSchoolListError);

  useEffect(() => {
    if (schoolList && schoolList.length) {
      setIsLoading(false);
    }

    return () => setIsLoading(false);
  }, [schoolList]);

  useEffect(() => {
    if (modalHasError) {
      setIsModalError(true);
      setIsLoading(false);
    }

    return () => {
      setIsModalError(false);
      setIsLoading(false);
    };
  }, [modalHasError]);

  const getBranding = () => {
    const { hasAK529, hasMD529, hasNAT529 } = productTypes;

    return hasAK529 && !(hasMD529 || hasNAT529)
      ? PRODUCT_TYPE_LABEL_MAP.ALASKA_529
      : hasMD529 && !(hasNAT529 || hasAK529)
      ? PRODUCT_TYPE_LABEL_MAP.MARYLAND_529
      : hasNAT529 && !(hasMD529 || hasAK529)
      ? PRODUCT_TYPE_LABEL_MAP.NATIONAL_529
      : 'plan';
  };

  const handleAddSchool = () => {
    if (isValidFields(school, errors)) {
      school.id = 'id' + Math.random().toString(36).substr(2, 9);
      Object.keys(school).forEach((k) => (school[k] = school[k].trim()));
      dispatch(addNewSchoolToStore(school));
      closeModal(ADD_SCHOOL_MODAL);
    }
  };

  const [errors, setErrors] = useState({
    schoolName: false,
    addressLineOne: false,
    addressLineTwo: false,
    city: false,
    state: false,
    zip: false,
    zipExt: false,
  });

  const runValidation = (validator, value) => {
    validator(value);
  };

  const handleSchoolNameBlur = (event) => {
    //Check if entered school name exists in the school list returned from API
    const foundSchool = find(
      schoolList,
      (school) => school.name === event.target.value
    );
    if (!foundSchool) {
      setIsAutoSuggestError(true);
    } else {
      setIsAutoSuggestError(false);
    }
  };

  const schoolNameValidator = (value) => {
    const isValid = handleMultipleValidations(
      value,
      addSchoolValidationFields.schoolName
    );
    const previousErrors = { ...errors };
    if (typeof isValid === 'string') {
      previousErrors.schoolName = isValid;
    }
    if (typeof isValid === 'boolean' && isValid) {
      previousErrors.schoolName = false;
    }
    setErrors({ ...previousErrors });
  };

  const addressLineOneValidator = (value) => {
    const isValid = handleMultipleValidations(
      value,
      addSchoolValidationFields.addressOne
    );
    const previousErrors = { ...errors };
    if (typeof isValid === 'string') {
      previousErrors.addressLineOne = isValid;
    }
    if (typeof isValid === 'boolean' && isValid) {
      previousErrors.addressLineOne = false;
    }
    setErrors({ ...previousErrors });
  };

  const addressLineTwoValidator = (value) => {
    const isValid = handleMultipleValidations(
      value,
      addSchoolValidationFields.addressTwo
    );
    const previousErrors = { ...errors };
    if (typeof isValid === 'string') {
      previousErrors.addressLineTwo = isValid;
    }
    if (typeof isValid === 'boolean' && isValid) {
      previousErrors.addressLineTwo = false;
    }
    setErrors({ ...previousErrors });
  };

  const cityValidator = (value) => {
    const isValid = handleMultipleValidations(
      value,
      addSchoolValidationFields.city
    );
    const previousErrors = { ...errors };
    if (typeof isValid === 'string') {
      previousErrors.city = isValid;
    }
    if (typeof isValid === 'boolean' && isValid) {
      previousErrors.city = false;
    }
    setErrors({ ...previousErrors });
  };

  const stateValidator = (value) => {
    const isValid = handleMultipleValidations(
      value,
      addSchoolValidationFields.state
    );
    const previousErrors = { ...errors };
    if (typeof isValid === 'string') {
      previousErrors.state = isValid;
    }
    if (typeof isValid === 'boolean' && isValid) {
      previousErrors.state = false;
    }
    setErrors({ ...previousErrors });
  };

  const zipValidator = (value) => {
    const isValid = handleMultipleValidations(
      value,
      addSchoolValidationFields.zipCode
    );
    const previousErrors = { ...errors };
    if (typeof isValid === 'string') {
      previousErrors.zip = isValid;
    }
    if (typeof isValid === 'boolean' && isValid) {
      previousErrors.zip = false;
    }
    setErrors({ ...previousErrors });
  };

  const zipExtValidator = (value) => {
    const isValid = handleMultipleValidations(
      value,
      addSchoolValidationFields.zipCodeExt
    );
    const previousErrors = { ...errors };
    if (typeof isValid === 'string') {
      previousErrors.zipExt = isValid;
    }
    if (typeof isValid === 'boolean' && isValid) {
      previousErrors.zipExt = false;
    }
    setErrors({ ...previousErrors });
  };

  const getClassName = (fieldName) =>
    fieldName ? 'box-error blocked_data' : 'input-success blocked_data';

  const chevronClassName = displayMessage
    ? 'chevron-style chevron-up'
    : 'chevron-style chevron-down';

  const [suggestions, setSuggestions] = useState([]);

  const onSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestions = (value) => {
    const inputValue = value.trim().toLowerCase();
    const suggestionsArray =
      inputValue.length === 0
        ? []
        : schoolList.filter(
            (item) =>
              item.name.toLowerCase().slice(0, inputValue.length) === inputValue
          );

    if (suggestionsArray.length > 25) {
      return suggestionsArray.slice(0, 25);
    }

    return suggestionsArray;
  };

  const getSuggestionValue = (suggestion) => suggestion.name;

  const renderSuggestion = (suggestion) => (
    <div className="new-fund-search-fund-enable">
      <div className="new-fund-search-suggestion-name-scs">
        <span className="new-fund-search-suggestion-ticker-scs">
          {suggestion.name}
        </span>
      </div>
    </div>
  );

  const onSuggestionSelected = () => {
    setIsAutoSuggestError(false);
  };

  const onChange = (event, { newValue }) => {
    setSchool({ ...school, schoolName: newValue });
    runValidation(schoolNameValidator, newValue);
  };

  const handleZipBlur = (event, id) => {
    if (event && event.target && event.target.value) {
      if (id === 'zip-ext') {
        runValidation(zipExtValidator, event.target.value);
      } else {
        runValidation(zipValidator, event.target.value);
      }
    }
  };

  const inputProps = {
    placeholder: 'Type an Institution Name',
    value: school.schoolName,
    onChange: onChange,
    onBlur: handleSchoolNameBlur,
  };

  if (isModalError) {
    return (
      <div data-testid="loading-container" className="loading-container">
        <ErrorPage
          hasError={true}
          heading={'We are having technical issues loading your page.'}
          retryMsg=""
          errorMsg={
            <div className="error-msg error-msg-lh">
              <span>
                Please{' '}
                <a
                  className="full-page-loader-link"
                  href={`${config.url}/personal-investing/help/contact-us.html`}
                >
                  call us
                </a>{' '}
                to speak with a representative or try again later.
              </span>
            </div>
          }
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="loading-container" className="loading-container">
        <Loader isVisible />
      </div>
    );
  }

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className="add-school-modal blocked_data">
        <h1>Enter Your Educational Institution:</h1>
        <Autosuggest
          className="blocked_data"
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          onSuggestionSelected={onSuggestionSelected}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
        />
        {errors.schoolName ? (
          <p className="error">{errors.schoolName}</p>
        ) : null}
        {isAutoSuggestError && !errors.schoolName && (
          <p className="error">Please Select a Valid Institution Name</p>
        )}
        <p>
          If your educational institution cannot be found in the list above,
          click the link below for more options.
        </p>
        <div className="school-not-found">
          <a
            id="chevron-up-down"
            className="dropdown-style"
            data-testid="chevron-up-down"
            onClick={() => setDisplayMessage(!displayMessage)}
          >
            Educational Institution Not Found
          </a>
          <img
            id="chevron-up-down"
            className={chevronClassName}
            onClick={() => setDisplayMessage(!displayMessage)}
            alt="chevron"
            src={icon_chevron_up}
          />
        </div>
        {displayMessage ? (
          <div>
            <p>
              The
              <a
                target="_blank"
                href="https://fsapartners.ed.gov/knowledge-center/library/resource-type/Federal%20School%20Code%20Lists"
                rel="noreferrer"
              >
                {' '}
                Integrated Postsecondary Education Data System{' '}
              </a>
              is used to source the educational institution selection list. For
              more information, visit their website at:
              <a
                href="https://www.nces.ed.gov/ipeds"
                rel="noreferrer"
                target="_blank"
              >
                {' '}
                www.nces.ed.gov/ipeds.{' '}
              </a>
            </p>
            <p>
              When you see this feature you will leave the {getBranding()}{' '}
              website and go to a third party website. We are not responsible
              for the content of or services provided by any third-party site.
            </p>
            <p>
              If you would like to make a distribution to an educational
              institution you cannot find in the educational institution list,
              you may <PhoneNumberBranding /> for assistance with this or other
              questions.
            </p>
            <p>
              You are also able to close this screen to return to Step 1 so you
              can choose a different delivery method.
            </p>
          </div>
        ) : (
          <></>
        )}
        <hr></hr>
        <h2>Please Enter the Institution's Mailing Information Below</h2>
        <div className="description-and-tooltip">
          <p>
            Be sure to provide the address where the check should be mailed,
            which most likely can be found on the most recent tuition bill you
            received. Please enter alphabetic and numeric characters only.
          </p>
          <ToolTip
            header={'Educational Institution Address'}
            message={toolTipMessage}
            placement={TOOLTIP_POSITION_BOTTOM}
            theme="education-tooltip"
          />
        </div>
        <small>All fields are required.</small>
        <div className="address-form">
          <h3>Address Line 1</h3>
          <input
            className={getClassName(errors.addressLineOne)}
            type="text"
            id="address-one"
            data-testid="address-one"
            required
            name="address-one"
            onChange={(event) => {
              const value = event.target.value;
              setSchool({ ...school, addressLineOne: value });
              runValidation(addressLineOneValidator, value.trim());
            }}
            value={school.addressLineOne}
          />
          {errors.addressLineOne ? (
            <p className="error">{errors.addressLineOne}</p>
          ) : null}
          <h3>Address Line 2</h3>
          <input
            className={getClassName(errors.addressLineTwo)}
            type="text"
            id="address-two"
            data-testid="address-two"
            name="address-two"
            onChange={(event) => {
              const value = event.target.value;
              setSchool({
                ...school,
                addressLineTwo: value,
              });
              runValidation(addressLineTwoValidator, value.trim());
            }}
            value={school.addressLineTwo}
          />
          {errors.addressLineTwo ? (
            <p className="error">{errors.addressLineTwo}</p>
          ) : null}
          <h3>City</h3>
          <input
            className={getClassName(errors.city)}
            type="text"
            id="city"
            required
            data-testid="city"
            name="city"
            onChange={(event) => {
              const value = event.target.value;
              setSchool({ ...school, city: value });
              runValidation(cityValidator, value.trim());
            }}
            value={school.city}
          />
          {errors.city ? <p className="error">{errors.city}</p> : null}
          <h3>State</h3>
          <div data-testid="state">
            <Dropdown
              styles={{ control: 'outline-color:red' }}
              required
              errorsRequired={true}
              initialError={initialError}
              options={us_states_abbr}
              id="state"
              data-testid="state"
              name="state"
              onChange={(event) => {
                setInitialError(true);
                const value = event?.value;
                setSchool({ ...school, state: value });
                runValidation(stateValidator, value);
              }}
            />
            {errors.state ? <p className="error">{errors.state}</p> : null}
          </div>
          <h3>Zip</h3>
          <div className="zip-code-input">
            <div className="zip-input-container">
              <input
                className={getClassName(errors.zip)}
                type="text"
                required
                id="zip"
                data-testid="zip"
                maxLength="5"
                name="zip"
                onBlur={(event) => handleZipBlur(event, 'zip')}
                onChange={(event) => {
                  setSchool({ ...school, zip: event.target.value });
                }}
                value={school.zip}
              />
              {errors.zip && (
                <div data-testid="zip-error" className="error-zip">
                  {errors.zip}
                </div>
              )}
            </div>
            <span className="zip-spacer">-</span>
            <div className="zip-input-container">
              <input
                className={getClassName(errors.zipExt)}
                type="text"
                required
                id="zip-ext"
                maxLength="4"
                data-testid="zip-ext"
                name="zip-ext"
                onBlur={(event) => handleZipBlur(event, 'zip-ext')}
                onChange={(event) => {
                  const value = event.target.value;
                  setSchool({
                    ...school,
                    zipExt: value ? value.trim() : value,
                  });
                }}
                value={school.zipExt}
              />
              {errors.zipExt && (
                <div data-testid="zip-ext-error" className="error-zip">
                  {errors.zipExt}
                </div>
              )}
            </div>
          </div>
          <div className="add-school-cancel-buttons">
            <Button
              applyBranding
              buttonType={ButtonType.SECONDARY_SLIM}
              className="add-school-cancel-btn"
              disabled={false}
              id="add-school-cancel-btn"
              data-testid="add-school-cancel-btn"
              onClick={() => closeModal(ADD_SCHOOL_MODAL)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              applyBranding
              buttonType={ButtonType.PRIMARY}
              className="add-school-submit-btn"
              disabled={!isValidFields(school, errors, isAutoSuggestError)}
              id="add-school-submit-btn"
              data-testid="add-school-submit-btn"
              onClick={handleAddSchool}
            >
              Add School
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddSchoolModal;
